/**
 * <DependencyApprovalModal> — user-gated approval surface for the
 * `requestDependency` MCP tool (Crucible #548).
 *
 * Specialist subagents (e.g. `3d-graphics-engineer` reaching for
 * `@react-three/fiber` + `three`) emit `mcp__foundry__requestDependency`
 * with a package name, optional version, scope, and one-line reason.
 * Main pushes the request via `deps:request`; this component renders
 * it as a modal with three actions:
 *
 *   - **Install** (primary)   — approve with the agent-requested version.
 *   - **Install with version pin…** (secondary, expandable) — approve
 *     with a tighter pin the user types into an inline input.
 *   - **Decline** (subdued)   — refuse the request.
 *
 * Esc / Back-button dismissal maps to Decline with a fixed reason.
 *
 * Visual posture: accent-bordered
 * container, generous typography on the package name (typosquatting
 * defence — the user reads the name unambiguously), italic reason
 * below, three buttons aligned right. Modal-style overlay (fixed
 * position, dim backdrop) because the agent's turn is blocked
 * waiting on the answer — the user shouldn't be invited to do
 * anything else with the chat panel in the meantime.
 *
 * Renderer-only: this component does not call any IPC directly. All
 * side effects flow through props (`onApprove` / `onDecline`); the
 * Workspace consumer wires them into chatDispatch which the chat
 * reducer's effect interpreter forwards onto the `deps:respond` IPC
 * channel.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

export interface DependencyApprovalModalProps {
  /** Bare package name, e.g. `@react-three/fiber`. */
  packageName: string;
  /** Concrete version string. Defaults to `'latest'` from main. */
  requestedVersion: string;
  /** Specialist's one-line justification (italic). */
  reason: string;
  /** Workspace scope the install lands in (e.g. `apps/display`). */
  scope: string;
  /** Epoch ms the request was fired. Reserved for future relative-time UX. */
  requestedAt: number;
  /** Called with the final version the user authorised (default or pin). */
  onApprove: (finalVersion: string) => void;
  /** Called with a short reason string (Decline / Esc / Backdrop). */
  onDecline: (reason: string) => void;
}

/**
 * Stable copy for the three decline paths. Exported so tests can pin
 * the wording — these strings end up in the agent's tool result and
 * drift would change live agent behaviour.
 */
export const DEPENDENCY_DECLINE_REASON = 'user clicked Decline';
export const DEPENDENCY_DISMISS_REASON = 'user dismissed the modal (Esc / backdrop)';

/**
 * Placeholder rendered in the command-preview block when pin mode is
 * on but the user hasn't entered a valid pin yet. Exported so tests
 * can pin the wording — drift here would change the user-visible
 * "what will run" surface that the entire modal is built around.
 *
 * Rationale for showing a placeholder rather than falling back to
 * `requestedVersion`: in pin mode the Install-pinned button is
 * disabled until the input is valid, so the preview MUST NOT show
 * `requestedVersion` (a clickable-looking command that doesn't
 * match the disabled button is the worst of both worlds — the
 * user reads it, doesn't notice the button is disabled, and
 * complains nothing happened). The placeholder visually decouples
 * the preview from any specific install command until the user
 * commits to a pin.
 */
export const DEPENDENCY_PREVIEW_EMPTY_PIN_PLACEHOLDER = '(enter a version above)';

/**
 * Defensive shape check on the pin input. Returns true when the
 * string is a plausible semver range / dist-tag / git ref shape and
 * carries no whitespace or shell metacharacters that the install
 * helper would reject downstream. Kept loose intentionally — pnpm
 * accepts a wide range of inputs, we just don't want a user typo
 * that contains a space to make it all the way to spawn.
 */
const PIN_FORBIDDEN = /[\x00-\x1F\x7F"'`$|&<>;\\(){}\[\]\s]/;

export function isValidVersionPin(pin: string): boolean {
  if (pin.length === 0) return false;
  if (pin.length > 200) return false;
  return !PIN_FORBIDDEN.test(pin);
}

export function DependencyApprovalModal({
  packageName,
  requestedVersion,
  reason,
  scope,
  requestedAt,
  onApprove,
  onDecline,
}: DependencyApprovalModalProps): React.JSX.Element {
  // Track whether the user has expanded the version-pin input. We
  // start collapsed so the Install button is the dominant path —
  // pinning is a power move, not the default.
  const [pinExpanded, setPinExpanded] = useState(false);
  const [pinValue, setPinValue] = useState(requestedVersion);

  // Suppress the unused-param warning on `requestedAt` — reserved for
  // a future relative-time UX (e.g. "(2s ago)"), but exposing it now
  // pins the prop shape so a future addition doesn't require a
  // breaking change to the consumer's onPush wiring.
  void requestedAt;

  // Auto-focus the dialog root on mount so Escape lands immediately
  // without the user having to click into the modal first. Macroscope
  // round-2 review on PR #581: without `tabIndex={-1}` + an explicit
  // focus() the keydown handler is dead code (focus stays on whatever
  // the user clicked last, which is some chat-panel button — Escape
  // there is a no-op). Mirrors the pattern react-aria's `<Dialog>`
  // uses for its initial-focus contract.
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const handleApproveDefault = (): void => {
    onApprove(requestedVersion);
  };

  const handleApprovePin = (): void => {
    const trimmed = pinValue.trim();
    if (!isValidVersionPin(trimmed)) return;
    onApprove(trimmed);
  };

  /**
   * Crucible #548 round-3 (Bugbot finding on `7a4cded1`): the
   * command-preview block must show EXACTLY what `onApprove` would
   * pass to the installer, never a stale `requestedVersion` when
   * the user is in pin mode with a different value typed.
   *
   * Three branches, mirroring the Install / Install-pinned approve
   * paths verbatim:
   *
   *   1. Pin mode OFF      → preview shows `requestedVersion` (the
   *      agent's original ask; Install button fires
   *      `onApprove(requestedVersion)`).
   *   2. Pin mode ON, pin valid → preview shows the trimmed pin
   *      value. Install-pinned fires `onApprove(trimmed)`.
   *   3. Pin mode ON, pin invalid (empty / whitespace / metachar) →
   *      preview shows `DEPENDENCY_PREVIEW_EMPTY_PIN_PLACEHOLDER`.
   *      Install-pinned is already disabled (`isValidVersionPin`
   *      guard); the placeholder visually decouples preview from
   *      "what would run", so the user can't read a stale command.
   *
   * Memoised on the inputs so a re-render driven by an unrelated
   * parent state change doesn't recompute it. Cheap either way —
   * just defensive about the .trim() + validity check showing up
   * in render output unconditionally.
   */
  const previewVersion = useMemo(() => {
    if (!pinExpanded) {
      return requestedVersion;
    }
    const trimmed = pinValue.trim();
    if (!isValidVersionPin(trimmed)) {
      return DEPENDENCY_PREVIEW_EMPTY_PIN_PLACEHOLDER;
    }
    return trimmed;
  }, [pinExpanded, pinValue, requestedVersion]);

  const handleDecline = (): void => {
    onDecline(DEPENDENCY_DECLINE_REASON);
  };

  // Esc anywhere inside the dialog → dismiss via Decline with the
  // distinct `DEPENDENCY_DISMISS_REASON` so the agent's tool result
  // can distinguish "user explicitly clicked Decline" from "user
  // walked away" if it ever wants to. Doc-block on this component
  // promised this surface; Bugbot + Macroscope both flagged it as a
  // dead contract pre-fix.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onDecline(DEPENDENCY_DISMISS_REASON);
    }
  };

  // Backdrop click → dismiss. The `target === currentTarget` check
  // is load-bearing: clicks on the card body bubble up to this
  // handler too, and without the discriminator a click on the
  // version-pin input would close the modal. We ONLY fire when the
  // click landed on the dimmed backdrop itself.
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onDecline(DEPENDENCY_DISMISS_REASON);
    }
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="deps-modal-title"
      data-testid="dependency-approval-modal"
      // tabIndex={-1} makes the div focusable via JS (.focus()) but
      // keeps it OUT of the natural Tab order — the Tab key still
      // walks the buttons / pin input inside, which is correct.
      // Required for the auto-focus + keydown wiring above.
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onClick={handleBackdropClick}
      // Codex review #4 MEDIUM (PR #646 / #643) — bumped from z-50
      // to z-[60] so this user-blocking modal paints above the
      // preview-recovery toast (z-[55]). The toast must surface for
      // an in-overlay restart failure (the overlay is z-50), so it
      // sits above the preview overlay; but a blocking modal MUST
      // beat the toast because it gates user interaction. Higher
      // z-index wins regardless of DOM order — leaving the modal at
      // z-50 would have let the toast paint above it.
      //
      // Issue #965 — `app-region-no-drag` defence-in-depth. The
      // Workspace's page-level `<header>` underneath carries
      // `-webkit-app-region: drag` via `app-region-drag` (see #639 +
      // #900). Drag-region hit-testing is OS-compositor level
      // (INDEPENDENT of visual z-index), so without this carve-out
      // the header strip absorbs clicks landing on the modal's
      // top strip — handle clicks (backdrop dismiss, Approve / Decline
      // buttons that overlap the header coords on small windows)
      // silently fail. Mirrors the PreviewOverlay (#847),
      // UpdateTemplateModal / CreateGameDialog / SsoLoginModal (#900)
      // carve-outs.
      className="app-region-no-drag fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4 outline-none"
    >
      <div className="w-full max-w-lg rounded-lg border border-crucible-accent/40 bg-zinc-950 px-5 py-5 shadow-2xl">
        <div className="mb-4">
          <p
            id="deps-modal-title"
            className="text-xs uppercase tracking-wide text-crucible-accent-hover"
          >
            Dependency add request
          </p>
          <p
            className="mt-1 break-all text-lg font-semibold text-white"
            data-testid="dependency-approval-modal-package"
          >
            {packageName}
            <span className="text-white/40">@</span>
            <span className="text-white/80">{requestedVersion}</span>
          </p>
          <p className="mt-2 text-xs text-white/60" data-testid="dependency-approval-modal-scope">
            Target: <span className="font-mono text-white/80">{scope}</span>
          </p>
        </div>

        <div className="mb-4 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wide text-white/40">Specialist reason</p>
          <p
            className="mt-1 text-sm italic text-white/80"
            data-testid="dependency-approval-modal-reason"
          >
            {reason}
          </p>
        </div>

        <p
          className="mb-4 text-xs leading-relaxed text-white/50"
          data-testid="dependency-approval-modal-command-preview"
        >
          Foundry will run{' '}
          <span className="font-mono text-white/70">
            pnpm --filter {scope} add {packageName}@
            <span
              data-testid="dependency-approval-modal-command-preview-version"
              // Distinct slot so tests can assert the version reactively
              // tracks pin mode without parsing the whole command line.
              // Italic when the placeholder is showing so the user reads
              // it as a prompt, not a literal command argument.
              className={
                previewVersion === DEPENDENCY_PREVIEW_EMPTY_PIN_PLACEHOLDER
                  ? 'italic text-amber-200/60'
                  : ''
              }
            >
              {previewVersion}
            </span>{' '}
            --ignore-scripts
          </span>{' '}
          inside the game directory. Postinstall scripts are skipped.
        </p>

        {pinExpanded && (
          <div
            className="mb-4 rounded-md border border-white/10 bg-black/30 px-3 py-2.5"
            data-testid="dependency-approval-modal-pin-row"
          >
            <label
              htmlFor="deps-modal-pin-input"
              className="text-[10px] uppercase tracking-wide text-white/40"
            >
              Version pin
            </label>
            <input
              id="deps-modal-pin-input"
              type="text"
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value)}
              data-testid="dependency-approval-modal-pin-input"
              className="mt-1 w-full rounded border border-white/10 bg-white/5 px-2 py-1 font-mono text-sm text-white/90 placeholder:text-white/30 focus:border-crucible-accent focus:outline-none"
              placeholder="e.g. ^9.0.0"
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-white/10 pt-3">
          <button
            type="button"
            onClick={handleDecline}
            data-testid="dependency-approval-modal-decline"
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/60 transition-colors hover:border-white/20 hover:text-white"
          >
            Decline
          </button>
          {pinExpanded ? (
            <button
              type="button"
              onClick={handleApprovePin}
              disabled={!isValidVersionPin(pinValue.trim())}
              data-testid="dependency-approval-modal-install-pin"
              className="rounded-lg bg-crucible-accent px-4 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Install pinned
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setPinExpanded(true)}
                data-testid="dependency-approval-modal-toggle-pin"
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/70 transition-colors hover:border-white/20 hover:text-white"
              >
                Install with version pin…
              </button>
              <button
                type="button"
                onClick={handleApproveDefault}
                data-testid="dependency-approval-modal-install"
                className="rounded-lg bg-crucible-accent px-4 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
              >
                Install
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
